import {
  IsBoolean,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  Min,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from './base.dto';

/**
 * Calendar-date contract for holiday dates: exactly `YYYY-MM-DD`, no time and
 * no timezone offset. `@IsDateString()` is deliberately NOT used — it accepts
 * offset datetimes (e.g. `2026-01-01T00:30:00+02:00`) that shift the stored
 * calendar day. The day is stored verbatim, so the input must be unambiguous.
 */
const CALENDAR_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const CALENDAR_DATE_MESSAGE =
  'date must be a calendar date in YYYY-MM-DD format';

export interface HolidayListDto extends BaseDto {
  name: string;
  description?: string | null;
  weekendsAreHolidays: boolean;
  federationId: string;
}

export class CreateHolidayListDto
  extends BaseCreateDto
  implements Partial<HolidayListDto>
{
  @IsUUID()
  federationId: string;

  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsBoolean()
  weekendsAreHolidays?: boolean;
}

export class UpdateHolidayListDto
  extends BaseUpdateDto
  implements Partial<HolidayListDto>
{
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  name?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsBoolean()
  weekendsAreHolidays?: boolean;
}

export interface HolidayDateDto extends BaseDto {
  holidayListId: string;
  // Carried as a Date by the entity (matches BaseDto's Date timestamps);
  // serialized as an ISO string at HTTP response time.
  date: string | Date;
  description?: string | null;
}

export class CreateHolidayDateDto
  extends BaseCreateDto
  implements Partial<HolidayDateDto>
{
  @IsUUID()
  holidayListId: string;

  /** Calendar date, exactly YYYY-MM-DD (no time, no offset). */
  @Matches(CALENDAR_DATE_REGEX, { message: CALENDAR_DATE_MESSAGE })
  date: string;

  @IsOptional()
  @IsString()
  description?: string;
}

export class UpdateHolidayDateDto
  extends BaseUpdateDto
  implements Partial<HolidayDateDto>
{
  /**
   * Parent holiday list, used to scope the mutation: the service rejects an id
   * that does not belong to this list (so a date cannot be edited out of its
   * list — and, once federation-scoped users can manage dates, out of its
   * federation). Sent by the client, which always edits dates within a list.
   */
  @IsUUID()
  holidayListId: string;

  @IsOptional()
  @Matches(CALENDAR_DATE_REGEX, { message: CALENDAR_DATE_MESSAGE })
  date?: string;

  @IsOptional()
  @IsString()
  description?: string;
}

export class SearchHolidayDatesDto {
  @IsUUID()
  holidayListId: string;

  /** When true, returns dates strictly before today. Otherwise returns today and future. */
  @IsOptional()
  @IsBoolean()
  past?: boolean;

  @IsOptional()
  @IsInt()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(500)
  take?: number;
}
